import type { ScanRun } from '../components/ScanHistory';

/**
 * "Where you stand", computed only from runs that are comparable.
 *
 * A named share is a measurement of ONE question set on ONE engine. The
 * headline used to be the newest run in range and its delta against the run
 * before it, whatever either of them asked. On a range of six sets over two
 * engines that printed "0/25 · -13" on a PDF: the weakest set on the weaker
 * engine, minus a different set on the other engine. Neither number was
 * measured. This module never produces a figure across two sets or two engines.
 *
 * Readiness is the exception, and stays one number: it belongs to a page, not
 * to a question set. Its delta still needs the same page on both sides.
 */

/** What a run asked, as the reader knows it: the set's name, or a short fingerprint. */
export function setLabel(r: ScanRun): string {
  const name = r.querySetName?.trim();
  if (name) return name;
  return r.questionSet ? `Unnamed set · ${r.questionSet.slice(0, 6)}` : 'Unnamed set';
}

/** Two runs asked the same questions: same set, and not two known, different fingerprints. */
function sameQuestions(a: ScanRun, b: ScanRun): boolean {
  if (setLabel(a) !== setLabel(b) || a.domain !== b.domain) return false;
  return !(a.questionSet && b.questionSet && a.questionSet !== b.questionSet);
}

export interface StandingCell {
  engine: string;
  latest: ScanRun;
  /** Change in "named" against the previous run of the same set on the same engine; null when there is none. */
  delta: number | null;
  runs: number;
}

export interface StandingGroup {
  /** The set, prefixed with its domain when the range spans several domains. */
  label: string;
  cells: Record<string, StandingCell>;
  lastAt: string;
}

export interface Standing {
  readiness: { value: number; delta: number | null; url: string } | null;
  /** Unprompted knowledge, per engine: a property of the engine, not of a set. */
  knows: Record<string, string>;
  engines: string[];
  groups: StandingGroup[];
  /** The one cell, when the range holds exactly one set on one engine — then a single headline is honest. */
  single: StandingCell | null;
}

const ENGINE_ORDER = ['openai', 'gemini'];

/** `rows` newest first, as the run list returns them. */
export function standingOf(rows: ScanRun[]): Standing {
  const multiDomain = new Set(rows.map((r) => r.domain)).size > 1;
  const labelOf = (r: ScanRun): string => (multiDomain ? `${r.domain} · ${setLabel(r)}` : setLabel(r));

  const groups = new Map<string, StandingGroup>();
  const knows: Record<string, string> = {};
  const engines = new Set<string>();

  for (const r of rows) {
    if (r.fpKnows && !(r.engine in knows)) knows[r.engine] = r.fpKnows;
    if (r.visTotal == null) continue;               // no visibility probe in this run
    engines.add(r.engine);
    const label = labelOf(r);
    let g = groups.get(label);
    if (!g) { g = { label, cells: {}, lastAt: r.createdAt }; groups.set(label, g); }
    const cell = g.cells[r.engine];
    if (!cell) {
      g.cells[r.engine] = { engine: r.engine, latest: r, delta: null, runs: 1 };
      continue;
    }
    cell.runs++;
    // The first earlier run of this pair is the "previous" one; a delta exists
    // only if it asked the same questions.
    if (cell.runs === 2 && sameQuestions(cell.latest, r) && cell.latest.visMentioned != null && r.visMentioned != null) {
      cell.delta = cell.latest.visMentioned - r.visMentioned;
    }
  }

  const newest = rows.find((r) => r.readinessScore != null) ?? null;
  const prevSamePage = newest ? rows.find((r) => r !== newest && r.url === newest.url && r.readinessScore != null) ?? null : null;

  const list = [...groups.values()].sort((a, b) => b.lastAt.localeCompare(a.lastAt));
  const cells = list.flatMap((g) => Object.values(g.cells));
  return {
    readiness: newest ? {
      value: newest.readinessScore!,
      delta: prevSamePage ? newest.readinessScore! - prevSamePage.readinessScore! : null,
      url: newest.url,
    } : null,
    knows,
    engines: ENGINE_ORDER.filter((e) => engines.has(e)).concat([...engines].filter((e) => !ENGINE_ORDER.includes(e))),
    groups: list,
    single: cells.length === 1 ? cells[0]! : null,
  };
}

/**
 * The range split into one chart per question set.
 *
 * One line across six sets joined six different measurements and then printed,
 * underneath, that the line could not be read. Each set now gets its own chart;
 * inside one, a point where the same set's questions were edited is still
 * marked, because that is a smaller version of the same problem.
 */
export function facetsOf(rows: ScanRun[]): { label: string; rows: ScanRun[] }[] {
  const multiDomain = new Set(rows.map((r) => r.domain)).size > 1;
  const out = new Map<string, ScanRun[]>();
  for (const r of rows) {
    if (r.visTotal == null && r.fpTotal == null) continue;
    const label = multiDomain ? `${r.domain} · ${setLabel(r)}` : setLabel(r);
    out.set(label, [...(out.get(label) ?? []), r]);
  }
  return [...out.entries()].map(([label, rs]) => ({ label, rows: rs }));
}
